import { useMemo, useState } from 'react';
import { LuSearch, LuX } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { HouseholdType, OpportunityView } from '@ndvr/customer-api';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { formatCurrencyShort } from 'utils/number-formatter';

import { OpportunityStatusBadge } from './OpportunityStatusBadge';

interface OpportunityFunnelDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    stageLabel: string;
    opportunities: OpportunityView[];
    householdMap: Map<string, { name: string; type?: HouseholdType }>;
}

export const OpportunityFunnelDrawer = (props: OpportunityFunnelDrawerProps) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredOpportunities = useMemo(() => {
        if (!searchTerm.trim()) return props.opportunities;

        const lowerSearch = searchTerm.toLowerCase();
        return props.opportunities.filter((opp) => {
            const householdName = props.householdMap.get(opp.householdGUID)?.name || '';
            return opp.name.toLowerCase().includes(lowerSearch) || householdName.toLowerCase().includes(lowerSearch);
        });
    }, [props.opportunities, searchTerm, props.householdMap]);

    const totalValue = useMemo(
        () => props.opportunities.reduce((sum, opp) => sum + (Number(opp.value) || 0), 0),
        [props.opportunities],
    );

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setSearchTerm('');
            props.onClose();
        }
    };

    return (
        <Sheet open={props.isOpen} onOpenChange={handleOpenChange}>
            <SheetContent className="flex w-full flex-col p-0 sm:max-w-lg">
                <SheetHeader className="border-b px-6 py-4">
                    <SheetTitle className="flex items-center justify-between">
                        <span>{props.stageLabel}</span>
                        <Badge variant="secondary" className="ml-2">
                            {props.opportunities.length}{' '}
                            {props.opportunities.length === 1 ? 'opportunity' : 'opportunities'}
                        </Badge>
                    </SheetTitle>
                    <SheetDescription>Total Value: {formatCurrencyShort(totalValue)}</SheetDescription>
                </SheetHeader>

                <div className="border-b px-6 py-3">
                    <div className="relative">
                        <LuSearch className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                        <Input
                            placeholder="Search by name or household..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pr-9 pl-9"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                            >
                                <LuX className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>

                <ScrollArea className="flex-1">
                    <div className="px-6 py-2">
                        {filteredOpportunities.length === 0 ? (
                            <div className="text-muted-foreground py-8 text-center">
                                {searchTerm ? 'No opportunities match your search' : 'No opportunities in this stage'}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredOpportunities.map((opp) => {
                                    const householdEntry = props.householdMap.get(opp.householdGUID);
                                    const householdType = householdEntry?.type;
                                    const householdName =
                                        householdEntry?.name ||
                                        (householdType === HouseholdType.PROSPECT
                                            ? 'Unknown Prospect'
                                            : 'Unknown Household');
                                    return (
                                        <div
                                            key={opp.opportunityGUID}
                                            className="bg-card hover:bg-muted/50 rounded-lg border p-4 transition-colors"
                                        >
                                            <div className="space-y-2">
                                                <h4 className="text-foreground leading-tight font-medium">
                                                    {opp.name}
                                                </h4>

                                                {opp.householdGUID && (
                                                    <Link
                                                        to={`/${householdType === HouseholdType.PROSPECT ? 'prospect' : 'household'}/${opp.householdGUID}`}
                                                        className="text-primary text-sm hover:underline"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {householdName}
                                                    </Link>
                                                )}

                                                <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                                                    <span className="inline-flex items-center">
                                                        <span className="text-foreground mr-1 font-medium">Value:</span>
                                                        {formatCurrencyShort(opp.value)}
                                                    </span>
                                                    <span className="inline-flex items-center">
                                                        <span className="text-foreground mr-1 font-medium">
                                                            Status:
                                                        </span>
                                                        <OpportunityStatusBadge status={opp.status} />
                                                    </span>
                                                </div>

                                                {opp.description && (
                                                    <p className="text-muted-foreground line-clamp-2 text-sm">
                                                        {opp.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </ScrollArea>
            </SheetContent>
        </Sheet>
    );
};
